<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { hasIncidentMasterAttribute } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import {
  Confirmation,
  IncidentActionType,
} from '@collector/sportsapi-client-legacy'
import {
  hasIncidentEmergencyCategory,
  isIncidentDeletable,
  isIncidentEditable,
} from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import Button from '@mobile/components/Button/Button.vue'
import DetailsButton from '@mobile/views/Relation/DetailsPanel/Buttons/DetailsButton.vue'
import { useSportOptions } from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { useEventRelationSettings } from '@mobile/views/Relation/DetailsPanel/Sports/useEventRelationSettings'
import IncidentName from '@mobile/views/Relation/DetailsPanel/Table/IncidentName.vue'
import PlayerSelect from '@mobile/views/Relation/DetailsPanel/Table/PlayerSelect/PlayerSelect.vue'
import HistoryItemTableRowIncTimeUpdate from '@mobile/views/Relation/DetailsPanel/Table/TimeUpdate/HistoryItemTableRowIncTimeUpdate.vue'
import { useHistoryIncidents } from '@mobile/views/Relation/DetailsPanel/Table/useHistoryIncidents'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { onClickOutside } from '@vueuse/core'

import { Result } from '../../Shared/IncidentResult'
import { useSportConfiguration } from '../../Shared/RelationSportCommonDependencies/useSportConfiguration'
import { useSwappableIncidents } from '../../useSwappableIncidents'
import SwapIncidentButton from '../Buttons/SwapIncidentButton.vue'
import { useIncidentModals } from '../composables/useIncidentModals'
import { isIncidentDeletableByResultConfiguration } from '../isIncidentDeletableByResultConfiguration'
import { formatDate } from '../utils'
import HistoryItemTableRowAttributes from './HistoryItemTableRowAttributes.vue'
import { DockedIncidentPosition } from './useDockedIncidentComponent'

type Props = {
  linkedIncident: ProbableLinkedIncident
  result: Result | null
  disabled?: boolean
  docked?: boolean
  pinned?: boolean
  selectIncidentOnModalOpen?: boolean
  position?: DockedIncidentPosition | null
  shouldFocusOnMinutesInput?: boolean
  openedMainSelect?: boolean
  openedSecondarySelect?: boolean
  openedTernarySelect?: boolean
}

const props = defineProps<Props>()
defineEmits(['pinIncident'])

const {
  openIncidentsDeleteModal,
  openIncidentsDetailModal,
  openSwapIncidentModal,
} = useIncidentModals()

function handleOpenDeleteModal(): void {
  openIncidentsDeleteModal(
    props.linkedIncident,
    props.selectIncidentOnModalOpen,
  )
}

function handleOpenDetailModal(): void {
  openIncidentsDetailModal(
    props.linkedIncident,
    props.selectIncidentOnModalOpen,
  )
}

function handleOpenSwapModal(): void {
  openSwapIncidentModal(props.linkedIncident, props.selectIncidentOnModalOpen)
}

const eventInjections = useEventInjections()
const { getConfigForIncident } = useSwappableIncidents()
const { sport } = eventInjections
const sportOptionsComposable = useSportOptions(sport)
const { eventClockConfiguration, incidentsResultConfiguration } =
  useSportConfiguration()
const {
  toggleIncidentPinState,
  isIncidentPinned,
  clearSelection,
  isIncidentSelected,
} = useHistoryIncidents(eventInjections)
const { participantsIncidents, detailsIncidents } = useEventRelationSettings(
  eventInjections,
  sportOptionsComposable,
)
const isDeletable = computed(
  () =>
    isIncidentDeletable(props.linkedIncident.incident, sport) &&
    isIncidentDeletableByResultConfiguration(
      props.linkedIncident.incident,
      incidentsResultConfiguration,
    ),
)

const time = computed(() => {
  const [minute, second] = props.linkedIncident.incident.event_time
    ?.split(':')
    .map(Number) ?? [0, 0]
  return { minute, second }
})
const showAssignParticipantsSelects = computed(() =>
  participantsIncidents.value.includes(
    props.linkedIncident.incident.incident_id,
  ),
)
const showDetailsButton = computed(() => {
  const hasConfiguration =
    !!sportOptionsComposable.detailsConfiguration[
      props.linkedIncident.incident.incident_id
    ]
  return (
    hasConfiguration &&
    detailsIncidents.value.includes(props.linkedIncident.incident.incident_id)
  )
})
const showAttributesCheckboxes = computed(() =>
  detailsIncidents.value.includes(props.linkedIncident.incident.incident_id),
)

const target = ref<HTMLElement | null>(null)
const classes = computed(() => {
  const css = props.linkedIncident.probableLinkedParticipant?.css
  const side = props.linkedIncident.probableLinkedParticipant?.side
  const defaultBackgroundColor = getDefaultBackgroundColor()
  const backgroundColorClasses = []
  if (css?.gradient32ToWhite) {
    backgroundColorClasses.push(css.gradient32ToWhite)
    if (!props.docked) {
      backgroundColorClasses.push(
        side === 'home'
          ? 'hover:bg-linear-to-r hover:from-home/40 hover:to-home/5'
          : 'hover:bg-linear-to-r hover:from-away/40 hover:to-away/5',
      )
    }
  } else {
    backgroundColorClasses.push(defaultBackgroundColor)
  }
  if (props.docked) {
    return [...backgroundColorClasses, 'absolute', 'z-80', 'docked']
  }
  return [
    ...backgroundColorClasses,
    isIncidentEditable(props.linkedIncident.incident, sport) && !props.pinned
      ? 'cursor-pointer'
      : 'cursor-default',
  ]
})

const styleAbsolutePosition = computed(() => {
  if (!props.position) {
    return null
  }
  return {
    top: `${props.position.top}px`,
    left: `${props.position.left}px`,
  }
})

const isEditable = computed(() =>
  isIncidentEditable(props.linkedIncident.incident, sport),
)

let clickOutsideCancellationHandler: (() => void) | null = null

function getDefaultBackgroundColor(): string {
  switch (true) {
    case hasIncidentEmergencyCategory(props.linkedIncident.incident, sport):
    case hasIncidentMasterAttribute(
      props.linkedIncident.incident as unknown as Incident,
    ):
      return 'bg-dimmedred-20'
    default:
      return 'bg-white'
  }
}

function isIncidentDeleted(): boolean {
  return props.linkedIncident.incident.action === IncidentActionType.DELETE
}

onMounted(() => {
  watch(
    () => props.docked,
    (docked) => {
      if (docked) {
        const ignore = [
          '#incidentDetailsModal',
          '#incidentDeleteModal',
          '#swapIncidentModal',
          '#vipIncidentTimeModal',
        ]
        clickOutsideCancellationHandler = onClickOutside(
          target,
          () => isIncidentSelected(props.linkedIncident) && clearSelection(),
          { ignore, controls: true },
        ).stop
      } else {
        clickOutsideCancellationHandler?.()
      }
    },
    { immediate: true },
  )
})

onBeforeUnmount(() => {
  clickOutsideCancellationHandler?.()
})
</script>

<template>
  <div
    ref="target"
    class="group table-row w-full"
    :class="classes"
    :style="styleAbsolutePosition"
    data-testid="details-panel-incident-row"
  >
    <div class="table-cell p-2 align-middle">
      <button
        v-if="isEditable"
        class="hover:text-blue h-full w-full group-hover:opacity-100"
        :class="{ 'opacity-0': !isIncidentPinned(linkedIncident) }"
        type="button"
        data-incident-pin-button
        @click.stop="toggleIncidentPinState(linkedIncident)"
      >
        <div style="width: 20px">
          <FontAwesomeIcon icon="thumbtack" />
        </div>
      </button>
    </div>

    <div
      class="table-cell p-2 align-middle"
      :class="{ 'line-through': isIncidentDeleted() }"
    >
      <div class="w-[80px] xl:w-[100px]">
        {{ formatDate(linkedIncident.incident.ut) }}
        <div class="font-bold xl:hidden">
          {{ linkedIncident.incident.event_status_name }}
        </div>
      </div>
    </div>

    <div
      class="hidden p-2 align-middle xl:table-cell"
      :class="{ 'line-through': isIncidentDeleted() }"
      data-testid="details-panel-event-status-row"
    >
      <div class="w-[80px]">
        {{ linkedIncident.incident.event_status_name }}
      </div>
    </div>

    <div
      v-if="eventClockConfiguration.isEnabled"
      class="table-cell p-2 align-middle"
      :class="{ 'line-through': isIncidentDeleted() }"
      data-testid="details-panel-event-time-row"
    >
      <div class="flex flex-col gap-1">
        <HistoryItemTableRowIncTimeUpdate
          :shouldFocusOnMinutesInput
          :docked
          :pinned
          :linkedIncident
        />
      </div>
    </div>

    <div class="table-cell p-2 align-middle">
      <FontAwesomeIcon
        v-if="linkedIncident.incident.confirmation === Confirmation.Confirmed"
        class="text-green mx-1"
        icon="check"
      />

      <FontAwesomeIcon
        v-else
        class="text-red mx-1"
        :class="{
          'opacity-0':
            linkedIncident.incident.confirmation !== Confirmation.Cancelled,
        }"
        icon="times"
      />
    </div>

    <div
      class="table-cell w-full p-2 align-middle"
      :class="{ 'line-through': isIncidentDeleted() }"
    >
      <div class="flex space-x-2 align-middle">
        <div class="flex w-1/3 space-x-2">
          <div
            class="flex h-full w-full items-center justify-between space-x-4"
          >
            <IncidentName
              v-bind="{
                linkedIncident,
                result,
                showParticipants: true,
                showAttributes: true,
              }"
            />

            <div class="grid content-center">
              <Button
                v-if="isDeletable"
                class="px-3 opacity-0 group-hover:opacity-100"
                :color="'red'"
                :size="'xs'"
                @click="handleOpenDeleteModal"
              >
                <FontAwesomeIcon :icon="'trash'" />
              </Button>
            </div>
          </div>
        </div>

        <div class="grid w-2/3 content-center px-4">
          <PlayerSelect
            v-if="showAssignParticipantsSelects"
            v-bind="{ linkedIncident, time }"
            :disabled="isIncidentDeleted()"
            :openedMainSelect="docked ? openedMainSelect : false"
            :openedSecondarySelect="docked ? openedSecondarySelect : false"
            :openedTernarySelect="docked ? openedTernarySelect : false"
          />

          <HistoryItemTableRowAttributes
            v-else-if="showAttributesCheckboxes && docked"
            :linkedIncident
          />
        </div>
      </div>
    </div>

    <div class="table-cell p-2 align-middle">
      <div class="flex w-[120px] space-x-2">
        <div class="w-1/3">
          <SwapIncidentButton
            v-if="getConfigForIncident(linkedIncident.incident.incident_id)"
            :linkedIncident
            @click="handleOpenSwapModal"
          />
        </div>

        <div class="w-2/3">
          <DetailsButton
            v-if="showDetailsButton"
            :linkedIncident
            @click="handleOpenDetailModal"
          />
        </div>
      </div>
    </div>
  </div>
</template>
